import { Card, Checkbox, Empty, Space, Spin, Tag, Typography } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileWordOutlined,
  MoreOutlined,
  PartitionOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { Button, Dropdown } from 'antd'
import './KnowledgeItemGrid.css'

export type KnowledgeGridItem = {
  id: number
  folder_id: number | null
  title: string
  index_status: string
  chunk_count: number
  quality_tier?: string
  redundancy_tier?: string
  relations_status?: string
  files_json?: { original_name?: string }[] | null
}

type Props = {
  rows: KnowledgeGridItem[]
  loading: boolean
  selectedRowKeys: number[]
  onSelectChange: (keys: number[]) => void
  folderNameMap: Map<number, string>
  onOpen: (id: number) => void
  onEdit: (id: number, files?: KnowledgeGridItem['files_json']) => void
  onViewChunks: (item: KnowledgeGridItem) => void
  onReindex: (id: number) => void
  onDelete: (id: number) => void
  onQuality?: (item: KnowledgeGridItem) => void
}

const statusColor: Record<string, string> = {
  indexed: 'green',
  pending: 'orange',
  stale: 'gold',
  error: 'red',
  empty: 'default',
}

const qualityLabel: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
  unknown: '未评',
}

function fileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() || ''
  if (ext === 'pdf') return <FilePdfOutlined className="kg-grid-icon kg-grid-icon--pdf" />
  if (ext === 'doc' || ext === 'docx' || ext === 'rtf') {
    return <FileWordOutlined className="kg-grid-icon kg-grid-icon--word" />
  }
  if (ext === 'xls' || ext === 'xlsx') {
    return <FileExcelOutlined className="kg-grid-icon kg-grid-icon--excel" />
  }
  if (ext === 'pptx') return <FilePptOutlined className="kg-grid-icon kg-grid-icon--ppt" />
  if (ext === 'md' || ext === 'markdown' || ext === 'html' || ext === 'htm') {
    return <FileOutlined className="kg-grid-icon kg-grid-icon--md" />
  }
  if (['png', 'jpg', 'jpeg', 'webp', 'tif', 'tiff', 'bmp'].includes(ext)) {
    return <FileImageOutlined className="kg-grid-icon kg-grid-icon--image" />
  }
  return <FileOutlined className="kg-grid-icon" />
}

export function KnowledgeItemGrid({
  rows,
  loading,
  selectedRowKeys,
  onSelectChange,
  folderNameMap,
  onOpen,
  onEdit,
  onViewChunks,
  onReindex,
  onDelete,
  onQuality,
}: Props) {
  if (loading && rows.length === 0) {
    return (
      <div className="kg-grid-loading">
        <Spin />
      </div>
    )
  }

  if (!loading && rows.length === 0) {
    return <Empty description="暂无文档" className="kg-grid-empty" />
  }

  const toggle = (id: number, checked: boolean) => {
    if (checked) onSelectChange([...selectedRowKeys, id])
    else onSelectChange(selectedRowKeys.filter((k) => k !== id))
  }

  return (
    <div className="kg-grid">
      {rows.map((row) => {
        const fileName = row.files_json?.[0]?.original_name || row.title
        const checked = selectedRowKeys.includes(row.id)
        const menuItems: MenuProps['items'] = [
          {
            key: 'chunks',
            icon: <PartitionOutlined />,
            label: '查看切片',
            disabled: !row.chunk_count,
            onClick: () => onViewChunks(row),
          },
          { key: 'index', label: '索引', onClick: () => onReindex(row.id) },
          ...(onQuality ? [{ key: 'quality', label: '质量', onClick: () => onQuality(row) }] : []),
          { type: 'divider' as const },
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: '删除',
            danger: true,
            onClick: () => onDelete(row.id),
          },
        ]
        return (
          <Card
            key={row.id}
            size="small"
            className={`kg-grid-card${checked ? ' kg-grid-card--selected' : ''}`}
            hoverable
            onClick={() => onOpen(row.id)}
          >
            <div className="kg-grid-card-head" onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={checked} onChange={(e) => toggle(row.id, e.target.checked)} />
              <Space size={4} className="kg-grid-card-actions">
                <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(row.id, row.files_json)} />
                <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                  <Button size="small" icon={<MoreOutlined />} aria-label="更多操作" />
                </Dropdown>
              </Space>
            </div>
            <div className="kg-grid-card-icon">{fileIcon(fileName)}</div>
            <Typography.Text
              className="kg-grid-card-title"
              ellipsis={{ tooltip: fileName }}
              onClick={() => onOpen(row.id)}
            >
              {fileName}
            </Typography.Text>
            <div className="kg-grid-card-meta">
              <Tag>{row.folder_id ? folderNameMap.get(row.folder_id) || '文件夹' : '未分类'}</Tag>
              <Tag color={statusColor[row.index_status] || 'default'}>{row.index_status}</Tag>
              {row.quality_tier ? (
                <Tag>{qualityLabel[row.quality_tier] || row.quality_tier}</Tag>
              ) : null}
            </div>
          </Card>
        )
      })}
    </div>
  )
}
